import React from 'react';
import { createPortal } from 'react-dom';
import { X, Eye, Edit2, Trash2, ChevronDown, CheckCircle, Calendar, Flag, AlertCircle, Clock, RefreshCw, XCircle } from 'lucide-react';
import api from '../api';
import Swal from 'sweetalert2';

export default function EmployeeTasksModal({ isOpen, onClose, employee, tasks, onViewTask, onUpdate, onEditTask, onDeleteTask }) {
  if (!isOpen || !employee) return null;

  const employeeTasks = tasks.filter(t => t.assigned_to === employee.id);

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await api.patch(`/admin/tasks/${taskId}/status`, { status: newStatus });
      Swal.fire({
        icon: 'success',
        title: 'Updated',
        text: 'Task status updated successfully',
        timer: 1500,
        showConfirmButton: false
      });
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error("Error updating priority:", err);
      Swal.fire('Error', 'Failed to update status', 'error');
    }
  };

  const formatStatus = (status) => {
    switch(status) {
      case 'overdue': return 'Overdue';
      case 'completed': return 'Completed';
      case 'in_progress': return 'In Progress';
      case 'not_done': return 'Not Done';
      default: return 'Pending';
    }
  };
  
  const getStatusColor = (status) => {
    switch(status) {
      case 'overdue': return { bg: 'bg-red-50', text: 'text-red-500', border: 'border-red-100', icon: AlertCircle };
      case 'completed': return { bg: 'bg-emerald-50', text: 'text-emerald-500', border: 'border-emerald-100', icon: CheckCircle };
      case 'in_progress': return { bg: 'bg-blue-50', text: 'text-blue-500', border: 'border-blue-100', icon: RefreshCw };
      case 'not_done': return { bg: 'bg-gray-50', text: 'text-red-500', border: 'border-red-200', icon: XCircle };
      default: return { bg: 'bg-amber-50', text: 'text-amber-500', border: 'border-amber-100', icon: Clock };
    }
  };

  const getPriorityStyle = (priority) => {
    switch(priority) {
      case 'urgent': return "bg-red-100/50 text-red-500 border border-red-100";
      case 'high': return "bg-orange-100/50 text-orange-500 border border-orange-100";
      case 'medium': return "bg-blue-100/50 text-blue-500 border border-blue-100";
      case 'low': return "bg-slate-100/50 text-slate-500 border border-slate-100";
      default: return "bg-slate-100/50 text-slate-500 border border-slate-100";
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-200 overflow-hidden">
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-slate-100">
          <div>
            <h2 className="text-xl font-black text-slate-800">Tasks for {employee.name}</h2>
            <p className="text-sm font-semibold text-slate-500 mt-1">{employeeTasks.length} Total Tasks</p>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors"
          >
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>

        {/* Body / Table */}
        <div className="overflow-y-auto p-6 bg-slate-50/50 flex-1 custom-scrollbar">
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-100 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                    <th className="px-6 py-4 w-1/3">TASK</th>
                    <th className="px-4 py-4">PRIORITY</th>
                    <th className="px-4 py-4">STATUS</th>
                    <th className="px-4 py-4">DUE DATE</th>
                    <th className="px-6 py-4 text-right">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {employeeTasks.length > 0 ? employeeTasks.map((row) => {
                    const statusConfig = getStatusColor(row.status);
                    return (
                      <tr key={row.id} className="hover:bg-slate-50/50 transition-colors group">
                        <td className="px-6 py-4">
                          <div className="text-[13px] font-black text-slate-800">{row.title}</div>
                          {row.category && (
                            <div className="text-[11px] font-bold text-blue-500 bg-blue-50 inline-block px-1.5 mt-1 rounded border border-blue-100 bg-opacity-50 lowercase">{row.category}</div>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-3 py-1 rounded-full text-[11px] font-black capitalize ${getPriorityStyle(row.priority)}`}>
                            {row.priority}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className={`relative flex items-center w-28 h-9 border rounded-lg shadow-sm transition-all focus-within:ring-2 focus-within:ring-purple-100 ${statusConfig.bg} ${statusConfig.border}`}>
                            <select 
                              value={row.status}
                              onChange={(e) => handleStatusChange(row.id, e.target.value)}
                              className={`w-full h-full bg-transparent pl-3 pr-8 py-0.5 text-[11px] font-black ${statusConfig.text} appearance-none cursor-pointer focus:outline-none uppercase shrink-0`}
                            >
                              <option value="pending" className="text-amber-500 font-bold">PENDING</option>
                              <option value="in_progress" className="text-blue-500 font-bold">IN PROGRESS</option>
                              <option value="completed" className="text-emerald-500 font-bold">COMPLETED</option>
                              <option value="not_done" className="text-red-500 font-bold">NOT DONE</option>
                              <option value="overdue" className="text-red-600 font-bold">OVERDUE</option>
                            </select>
                            <ChevronDown size={14} className={`absolute right-3 pointer-events-none ${statusConfig.text}`} />
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`flex items-center gap-1.5 text-[12px] font-bold ${row.status === 'overdue' ? 'text-red-500' : 'text-slate-500'}`}>
                            <Calendar size={12} /> {new Date(row.due_date).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-3 text-slate-400 opacity-80 group-hover:opacity-100 transition-opacity">
                            <button 
                              className="hover:text-purple-600 transition-colors"
                              title="View Details"
                              onClick={() => { onClose(); onViewTask(row); }}
                            >
                              <Eye size={16} strokeWidth={2.5} />
                            </button>
                            <button 
                              className="hover:text-blue-600 transition-colors" 
                              title="Edit Task"
                              onClick={() => { onClose(); if(onEditTask) onEditTask(row); }}
                            >
                              <Edit2 size={16} strokeWidth={2.5} />
                            </button>
                            <button 
                              className="hover:text-red-600 transition-colors" 
                              title="Delete Task"
                              onClick={() => { if(onDeleteTask) onDeleteTask(row.id); }}
                            >
                              <Trash2 size={16} strokeWidth={2.5} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan="5" className="text-center py-10 font-bold text-slate-400">No tasks assigned to {employee.name}.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
